/**
 * OtpInput — split-box numeric verification code entry.
 *
 * Renders `length` individual single-digit input boxes wired together
 * so that they behave as a single "code" control:
 *   - typing a digit advances focus to the next box
 *   - Backspace on an empty box moves focus to (and clears) the prior box
 *   - ArrowLeft / ArrowRight move between boxes
 *   - pasting anywhere distributes digits across the boxes, ignoring
 *     spaces / hyphens / any non-digit characters
 *   - reaching `length` digits triggers `onComplete(value)` once — the
 *     caller wires this to auto-submit (omit it for a submit button)
 *
 * Accessibility: the group is labelled by `label`; each box is named
 * "`digitLabel` N of `length`" (default "Digit 1 of 6"). `invalid` marks
 * the boxes invalid when the caller renders its own error message; `error`
 * does that AND renders the message here.
 *
 * Value protocol:
 *   - `value` is always a concatenated string of digits, up to `length`.
 *   - `onChange(next)` is called with that same shape; the parent owns
 *     the state.
 *
 * iOS SMS one-time-code auto-fill:
 *   - `autoComplete="one-time-code"` is set on the first box only.
 *   - When iOS fills that box with the full code, our onChange treats
 *     the multi-digit input as a paste and distributes it.
 */

import React, { useEffect, useId, useRef } from 'react';

function onlyDigits(s) {
  return String(s || '').replace(/\D/g, '');
}

function OtpInput({
  value = '',
  onChange,
  onComplete,
  length = 6,
  disabled = false,
  autoFocus = false,
  error,
  label = 'Verification code',
  name = 'otp',
  digitLabel = 'Digit',
  invalid = false,
}) {
  const uid = useId();
  const groupId = `${uid}-${name}`;
  const errorId = error ? `${groupId}-err` : undefined;
  const isInvalid = !!error || invalid;

  const inputsRef = useRef([]);
  const setInputRef = (i) => (el) => { inputsRef.current[i] = el; };

  const digits = onlyDigits(value).slice(0, length).padEnd(length, ' ').split('');

  useEffect(() => {
    if (autoFocus && !disabled) {
      // Focus AND select, like arrow-key focus: after a rejected code the
      // boxes keep their digits and the next keystroke overwrites box 1.
      const first = inputsRef.current[0];
      if (first) {
        first.focus();
        try { first.select?.(); } catch { /* jsdom edge case */ }
      }
    }
  }, [autoFocus, disabled]);

  function emit(next) {
    const clean = onlyDigits(next).slice(0, length);
    const prevClean = onlyDigits(value).slice(0, length);
    if (clean !== prevClean) onChange?.(clean);
    if (
      typeof onComplete === 'function'
      && clean.length === length
      && prevClean.length !== length
    ) {
      // Pass the value directly — callers don't need to read it from
      // their own state, which may not have committed yet.
      onComplete(clean);
    }
  }

  function focusBox(i) {
    const el = inputsRef.current[i];
    if (el) {
      el.focus();
      // Select the (possibly empty) content so the next keystroke
      // overwrites rather than appending after a caret.
      try { el.select?.(); } catch { /* jsdom edge case */ }
    }
  }

  function handleChange(i, e) {
    const raw = e.target.value;
    const clean = onlyDigits(raw);

    // Multi-digit landing in one box (autofill / paste into a slot):
    // treat it as a paste starting at position `i`.
    if (clean.length > 1) {
      const current = onlyDigits(value).slice(0, length);
      const before = current.slice(0, i);
      const merged = (before + clean).slice(0, length);
      emit(merged);
      const nextIdx = Math.min(merged.length, length - 1);
      focusBox(nextIdx);
      return;
    }

    const current = onlyDigits(value).slice(0, length).split('');
    while (current.length < length) current.push('');
    current[i] = clean;                       // '' or single digit
    const next = current.join('').replace(/\s+/g, '');
    emit(next);

    if (clean && i < length - 1) focusBox(i + 1);
  }

  function handleKeyDown(i, e) {
    if (e.key === 'Backspace') {
      const box = e.currentTarget;
      if (!box.value && i > 0) {
        // Clear the previous box and move focus there.
        e.preventDefault();
        const current = onlyDigits(value).slice(0, length).split('');
        while (current.length < length) current.push('');
        current[i - 1] = '';
        emit(current.join(''));
        focusBox(i - 1);
      }
      return;
    }
    if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault();
      focusBox(i - 1);
      return;
    }
    if (e.key === 'ArrowRight' && i < length - 1) {
      e.preventDefault();
      focusBox(i + 1);
      return;
    }
    if (e.key === 'Home') { e.preventDefault(); focusBox(0); return; }
    if (e.key === 'End')  { e.preventDefault(); focusBox(length - 1); }
  }

  function handlePaste(i, e) {
    const text = e.clipboardData?.getData('text') ?? '';
    const clean = onlyDigits(text);
    if (!clean) return;
    e.preventDefault();
    const current = onlyDigits(value).slice(0, length);
    const before = current.slice(0, i);
    const merged = (before + clean).slice(0, length);
    emit(merged);
    const nextIdx = Math.min(merged.length, length - 1);
    focusBox(nextIdx);
  }

  function handleFocus(_i, e) {
    // Select content on focus so a stray click into a filled box lets
    // the user overtype without needing to backspace first.
    try { e.target.select?.(); } catch { /* noop */ }
  }

  return (
    <div className="auth-field">
      {label && (
        <label htmlFor={`${groupId}-0`} className="auth-field-label">
          {label}
        </label>
      )}
      <div
        role="group"
        aria-label={label}
        aria-describedby={errorId}
        className={`auth-otp-boxes${isInvalid ? ' is-invalid' : ''}`}
      >
        {Array.from({ length }).map((_, i) => {
          const ch = digits[i];
          const val = ch && ch !== ' ' ? ch : '';
          return (
            <input
              key={i}
              ref={setInputRef(i)}
              id={`${groupId}-${i}`}
              name={`${name}-${i}`}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              maxLength={1}
              value={val}
              disabled={disabled}
              onChange={(e) => handleChange(i, e)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={(e) => handlePaste(i, e)}
              onFocus={(e) => handleFocus(i, e)}
              aria-label={`${digitLabel} ${i + 1} of ${length}`}
              aria-invalid={isInvalid || undefined}
              aria-describedby={errorId}
              className={`auth-otp-box${isInvalid ? ' is-invalid' : ''}`}
              spellCheck={false}
              autoCorrect="off"
              data-testid={`otp-box-${i}`}
            />
          );
        })}
      </div>
      {error && (
        <p id={errorId} className="auth-field-error" aria-live="assertive">
          {error}
        </p>
      )}
    </div>
  );
}

export default OtpInput;
